import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { CSSTransition } from "react-transition-group";
import tinycolors from "tinycolor2";
import { style } from "typestyle";
import { CommonHeaderLayoutProps } from "../../types/index.js";
import {
  useIntersection,
  useKeyDown,
  useScrollDirection,
} from "../../utils/hooks.js";
import {
  getEmailLink,
  getPhoneLink,
  getQuickLinksWithIcon,
  getWhatsAppLink,
  onTransitionEnd,
  overrideImagesAspectRatio,
} from "../../utils/utils.js";
import Icon from "../Icon.js";
import MainMenuListCollapsible, {
  verticalMenuClassNames,
} from "../MainMenuListCollapsible.js";
import ImagesModule from "../Modules/ImagesModule.js";
import SmartLink from "../SmartLink.js";
import HamburgerButton from "./HamburgerButton.js";
import MobileLanguageMenu from "./MobileLanguageMenu.js";
import MobileMainLogo from "./MobileMainLogo.js";
import QuickButtons from "./QuickButtons.js";

const Header4And5Mobile: FunctionComponent<CommonHeaderLayoutProps> = ({
  accommodation,
  actionLinks,
  activeModuleId,
  activePagePath,
  fallbackLanguageId,
  imageOverlayGradient: overlay,
  imagesModule: imagesModuleProp,
  isPreview,
  languageId,
  logo,
  logoSize,
  mainPageURL,
  menuItems,
  pageId,
  pages,
  queries,
  scheme,
  sticky,
  whatsAppNumber,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const scrollDirection = useScrollDirection();
  const ref = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(ref);
  const isIntersecting = intersection?.isIntersecting ?? true;
  const imagesModule = imagesModuleProp
    ? overrideImagesAspectRatio(imagesModuleProp)
    : undefined;

  useKeyDown("Escape", () => setIsMenuOpen(false));

  const isFixed =
    !isPreview &&
    ((scrollDirection === "up" && !isIntersecting) || sticky || isMenuOpen);

  const showQuickButtons =
    isPreview ||
    isIntersecting ||
    sticky ||
    isMenuOpen ||
    scrollDirection === "up";

  const isHeaderBarOpaque =
    !isIntersecting || isMenuOpen || !imagesModule?.pageId;

  const quickNav = [
    getEmailLink(accommodation, languageId),
    ...(whatsAppNumber ? [getWhatsAppLink(whatsAppNumber)] : []),
    ...getQuickLinksWithIcon(actionLinks),
  ];

  const phoneLink = getPhoneLink(accommodation, languageId);

  return (
    <div className="HeaderModule__Inner--narrow">
      <header
        className={ClassNames(
          "Header4And5Mobile",
          `Header4And5Mobile--height-${logoSize}`,
          {
            "Header4And5Mobile--scrolled-down": !isIntersecting,
            "Header4And5Mobile--image-overlay-dark": overlay === "dark",
            "Header4And5Mobile--image-overlay-light": overlay === "light",
            "Header4And5Mobile--menu-open": isMenuOpen,
            "Header4And5Mobile--no-image": !imagesModule?.pageId,
          },
          style({
            background: scheme.main.background,
            color: scheme.main.text,
          }),
        )}
      >
        <div
          ref={ref}
          className={ClassNames("Header4And5Mobile__IntersectionHelper", {
            "Header4And5Mobile__IntersectionHelper--scrolled-down":
              !isIntersecting,
          })}
        />
        {imagesModule?.pageId && (
          <ImagesModule
            translatedModule={imagesModule}
            isPreview={isPreview}
            pageId={pageId}
            queries={queries}
            isActive={false}
            isInsideHeader={true}
            isFirstOnPage={false}
            parentScheme={scheme}
            activeModuleId={activeModuleId}
            fullHeight={true}
          />
        )}
        <CSSTransition
          timeout={300}
          in={isFixed}
          addEndListener={onTransitionEnd}
          classNames={{
            enter: "Header4And5Mobile__Bar--enter",
            enterActive: "Header4And5Mobile__Bar--enter-active",
            exit: "Header4And5Mobile__Bar--fixed Header4And5Mobile__Bar--exit",
            exitActive:
              "Header4And5Mobile__Bar--fixed Header4And5Mobile__Bar--exit-active",
          }}
        >
          <div
            className={ClassNames("Header4And5Mobile__Bar", {
              "Header4And5Mobile__Bar--fixed": isFixed,
              "Header4And5Mobile__Bar--transparent": !isHeaderBarOpaque,
              [style({ background: scheme.main.background })]:
                isHeaderBarOpaque,
              "Header4And5Mobile__Bar--scrolled-down": !isIntersecting,
            })}
          >
            <div className="Header4And5Mobile__BarInner Module__Wrapper">
              <div className="Header4And5Mobile__MenuButtonContainer">
                <HamburgerButton
                  className="Header4And5Mobile__HamburgerButton"
                  isMenuOpen={isMenuOpen}
                  languageId={languageId}
                  onClick={() => setIsMenuOpen((v) => !v)}
                />
              </div>
              <div className="Header4And5Mobile__LogoContainer">
                <MobileMainLogo
                  accommodationName={accommodation.name}
                  isPreview={isPreview}
                  logo={logo}
                  mainPageURL={mainPageURL}
                  isSmall={!isIntersecting}
                  size={logoSize}
                />
              </div>
              <div className="Header4And5Mobile__PhoneContainer">
                <a
                  href={phoneLink.href}
                  className="BareBtn Header4And5Mobile__Phone"
                  title={phoneLink.title}
                >
                  <Icon glyph={phoneLink.icon} />
                </a>
              </div>
            </div>
          </div>
        </CSSTransition>

        <CSSTransition
          in={isMenuOpen}
          timeout={2000}
          mountOnEnter={true}
          unmountOnExit={true}
          addEndListener={onTransitionEnd}
          classNames={{
            enterActive: "Header4And5Mobile__Menu--open",
            enterDone: "Header4And5Mobile__Menu--open",
          }}
        >
          <div
            className={ClassNames(
              "Header4And5Mobile__Menu",
              style({
                background: tinycolors(scheme.main.background)
                  .darken(5)
                  .toRgbString(),
              }),
            )}
          >
            <RemoveScroll
              enabled={!isPreview}
              className="Header4And5Mobile__MenuInner"
            >
              <MobileLanguageMenu
                className="Header4And5Mobile__LanguageMenu"
                isPreview={isPreview}
                languageId={languageId}
                pageId={pageId}
                onClick={() => setIsMenuOpen(false)}
              />
              <MainMenuListCollapsible
                isPreview={isPreview}
                languageId={languageId}
                onMenuItemClick={() => setIsMenuOpen(false)}
                menuItems={menuItems}
                pages={pages}
                activePagePath={activePagePath}
                fallbackLanguageId={fallbackLanguageId}
                menuLevel={0}
                collapseLevel={0}
                classNames={verticalMenuClassNames}
              />
              <ul className="Header4And5Mobile__QuickNav">
                {quickNav.map(({ href, icon, type }) => (
                  <li key={type} className="Header4And5Mobile__QuickNavItem">
                    <SmartLink
                      isPreview={isPreview}
                      anchorAttrs={{
                        href,
                        className: "Header4And5Mobile__QuickNavLink",
                        onClick: () => type !== "email" && setIsMenuOpen(false),
                      }}
                    >
                      <Icon
                        glyph={icon}
                        className="Header4And5Mobile__QuickNavIcon"
                      />
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </RemoveScroll>
          </div>
        </CSSTransition>
      </header>

      <QuickButtons
        actionLinks={actionLinks}
        className={style({
          background: scheme.primary.background,
          color: scheme.primary.text,
        })}
        isPreview={isPreview}
        isVisible={showQuickButtons}
        onClick={() => setIsMenuOpen(false)}
      />
    </div>
  );
};

export default Header4And5Mobile;
